import { useEffect, useState, useRef } from "react";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { fetchUserAbsenceStats } from "../../../api/user";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMNotice } from "../../utils/notifications";
import {
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

export const AbsenceStatsSection = ({ user, period }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  // Use same role check as parent component (absence:read) to determine admin access
  const isAdmin = useUserHasAccess({ role: "absence", action: "read" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const companyName = useSelector((state) => state.session.currentCompany?.name || "selskapet");
  const [stats, setStats] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const lastUserIdRef = useRef(null);
  const lastPeriodRef = useRef(null);
  const hasLoadedRef = useRef(false);

  // If admin and user is selected, use that user's ID, otherwise use current user's ID
  const user_id = isAdmin && user?.id ? user.id : currentUser?.id;

  const getStats = async () => {
    if (!user_id) return;
    
    setIsLoading(true);
    setError(false);
    try {
      const params = {};
      if (period?.from) params.from = period.from;
      if (period?.to) params.to = period.to;
      
      const response = await fetchUserAbsenceStats(user_id, params);
      if (response.status !== 200) {
        throw new Error(`Server error: ${response.status}`);
      }
      setStats(response?.data);
      lastUserIdRef.current = user_id;
      lastPeriodRef.current = period ? JSON.stringify(period) : null;
      hasLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching absence stats:", err);
      toast.error("Something went wrong when trying to load the stats");
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch stats when user_id or period changes or on initial load
    const periodKey = period ? JSON.stringify(period) : null;
    if (user_id && (user_id !== lastUserIdRef.current || periodKey !== lastPeriodRef.current || !hasLoadedRef.current)) {
      getStats();
    }
  }, [user_id, period]); // Dependency array ensures this runs when user_id or period changes

  const Stats = () => {
    if (!stats) return null;

    const overall = stats?.overall;
    const absencePercentage = overall?.absence_percentage_this_year || 0;
    const expectedDays = overall?.expected_work_days_this_year || 0;
    const absenceDays = overall?.total_absence_days_this_year || 0;
    const absenceDaysIncludingVacation = overall?.total_absence_days_including_vacation || 0;
    const vacationDays = stats?.vacation?.used_days || 0;
    
    // Calculate percentage including vacation
    const absencePercentageIncludingVacation = expectedDays > 0 
      ? ((absenceDaysIncludingVacation / expectedDays) * 100).toFixed(1)
      : 0;

    const StatCard = ({ title, icon: Icon, children, gradientFrom, gradientTo, error }) => {
      if (error) {
        return (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-900">{title}</h3>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {Icon && <Icon className={`h-6 w-6 ${gradientTo.includes('blue') ? 'text-blue-600' : gradientTo.includes('green') ? 'text-green-600' : gradientTo.includes('yellow') ? 'text-yellow-600' : 'text-purple-600'} mr-3`} />}
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            </div>
          </div>
          <div className="space-y-2">{children}</div>
        </div>
      );
    };

    const StatItem = ({ label, value, unit = "" }) => (
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-lg font-semibold text-gray-900">
          {value}
          {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
        </span>
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Overall Absence Percentage Card */}
        {overall && !overall.error && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <UserIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Fraværsprosent</h3>
                  <p className="text-sm text-gray-600">Beregnet basert på faktiske arbeidsdager</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-blue-600">{absencePercentage}%</div>
                <div className="text-sm text-gray-600 mt-1">
                  {absenceDays} av {expectedDays} dager
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="grid grid-cols-2 gap-4">
                <StatItem label="Forventede arbeidsdager" value={expectedDays} unit="dager" />
                <StatItem label="Fraværsdager" value={absenceDays} unit="dager" />
              </div>
              
              {/* Additional information section */}
              <div className="mt-4 pt-4 border-t border-blue-200 space-y-2">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{absencePercentageIncludingVacation}%</span> med ferie ({vacationDays} feriedager)
                </div>
                {isAdmin && companyName && (
                  <div className="text-sm text-gray-600">
                    Totalt for <span className="font-medium">{companyName}</span>: <span className="font-medium">{absencePercentage}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Absence Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Egenmelding"
            icon={ExclamationTriangleIcon}
            gradientFrom="from-yellow-50"
            gradientTo="to-yellow-100"
            error={stats?.self_certified?.error}
          >
            {stats?.self_certified && !stats.self_certified.error && (
              <>
                <StatItem label="Tillatt" value={stats.self_certified.allowed_days} unit="dager" />
                <StatItem label="Brukt" value={stats.self_certified.used_days} unit="dager" />
                <StatItem label="Gjenstående" value={stats.self_certified.remaining_days} unit="dager" />
                {stats.self_certified.max_occurrences != null && (
                  <>
                    <div className="pt-2 mt-2 border-t border-yellow-200">
                      <StatItem label="Maks forekomster" value={stats.self_certified.max_occurrences} />
                      <StatItem label="Brukte forekomster" value={stats.self_certified.used_occurrences} />
                    </div>
                  </>
                )}
              </>
            )}
          </StatCard>

          <StatCard
            title="Hjemme med sykt barn"
            icon={UserIcon}
            gradientFrom="from-green-50"
            gradientTo="to-green-100"
            error={stats?.child_sick_leave?.error}
          >
            {stats?.child_sick_leave && !stats.child_sick_leave.error && (
              <>
                <StatItem label="Tillatt" value={stats.child_sick_leave.allowed_days} unit="dager" />
                <StatItem label="Brukt" value={stats.child_sick_leave.used_days} unit="dager" />
                <StatItem label="Gjenstående" value={stats.child_sick_leave.remaining_days} unit="dager" />
              </>
            )}
          </StatCard>

          <StatCard
            title="Ferie"
            icon={CalendarIcon}
            gradientFrom="from-blue-50"
            gradientTo="to-blue-100"
            error={stats?.vacation?.error}
          >
            {stats?.vacation && !stats.vacation.error && (
              <>
                <StatItem label="Tillatt" value={stats.vacation.allowed_days} unit="dager" />
                <StatItem label="Brukt" value={stats.vacation.used_days} unit="dager" />
                <StatItem label="Gjenstående" value={stats.vacation.remaining_days} unit="dager" />
              </>
            )}
          </StatCard>

          <StatCard
            title="Avspasering"
            icon={ClockIcon}
            gradientFrom="from-purple-50"
            gradientTo="to-purple-100"
            error={stats?.compensatory_time_off?.error}
          >
            {stats?.compensatory_time_off && !stats.compensatory_time_off.error && (
              <>
                <StatItem label="Opptjent i fjor" value={stats.compensatory_time_off.earned_hours_last_year} unit="t" />
                <StatItem label="Opptjent i år" value={stats.compensatory_time_off.earned_hours_this_year} unit="t" />
                <StatItem label="Brukt i år" value={stats.compensatory_time_off.used_hours_this_year} unit="t" />
                <div className="pt-2 mt-2 border-t border-purple-200">
                  <StatItem label="Gjenstående" value={stats.compensatory_time_off.earned_hours_left} unit="t" />
                </div>
              </>
            )}
          </StatCard>

          <StatCard
            title="Sykemelding"
            icon={ExclamationTriangleIcon}
            gradientFrom="from-red-50"
            gradientTo="to-red-100"
            error={stats?.sick_leave?.error}
          >
            {stats?.sick_leave && !stats.sick_leave.error && (
              <>
                <StatItem label="Brukt" value={stats.sick_leave.used_days || 0} unit="dager" />
                {stats.sick_leave.note && (
                  <div className="pt-2 mt-2 border-t border-red-200">
                    <p className="text-xs text-gray-600 italic">{stats.sick_leave.note}</p>
                  </div>
                )}
              </>
            )}
          </StatCard>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
            {isLoading ? (
              <ResourceLoading size="small" />
            ) : error ? (
              <PMNotice title="Kunne ikke laste inn stats" />
            ) : (
              <Stats />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
